import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { dateShort } from "../lib/ct";

// The Rainmaker · Invitations (build brief §1–2). Five cohorts over one table, a headline that answers
// the four questions in a sentence, and one irreversible action — mail to a real inbox — behind an
// intent-preview-and-confirm compose step. No bulk, no cap, no nudge button: R1 is hand-picked and the
// chase is a phone call. The worklist and the Treasurer's funnel read ONE snapshot (§5).
const WORD = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve"];
const w = (n) => (n <= 12 ? WORD[n] : String(n));

// F134 (founder ruling 11 Oct 2026): ONE date format everywhere, in CENTRAL time — see lib/ct.js.
// Publish C rendered UTC here, so an invitation issued in the Houston evening read as the next day.
function daysLeft(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - Date.now()) / 86400000);
}

const CHIPS = [
  ["everything", "Everything", null],
  ["live", "Invitation live", "live"],
  ["ran_out", "Ran out", "ran_out"],
  ["filed_nothing", "In, filed nothing", "filed_nothing"],
  ["gone_quiet", "Gone quiet", "gone_quiet"],
];

// `initial` pre-fills the panel when compose is opened FROM a row (an invitation request, which
// already knows the email and the seat). The header button opens it empty. Same panel either way,
// by founder ruling 12 Oct 2026: intent-preview-and-confirm is not optional on an issue, so there is
// no path that mails anyone without this screen having been seen and a name having been typed.
function Compose({ onClose, onSent, authedFetch, initial }) {
  const [name, setName] = useState((initial && initial.name) || "");
  const [email, setEmail] = useState((initial && initial.email) || "");
  const [note, setNote] = useState("");
  const seat = (initial && initial.seat) || "owner";
  const [preview, setPreview] = useState({ html: "", expires_str: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ready = name.trim() && email.trim();
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      authedFetch("/identity/staff/invite/preview",
        { method: "POST", body: { name: name.trim(), email: email.trim(), note } })
        .then(setPreview).catch(() => {});
    }, 250);
    return () => clearTimeout(timer.current);
  }, [name, email, note, authedFetch]);

  async function send() {
    if (!ready || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await authedFetch("/identity/staff/invite",
        { method: "POST", body: { name: name.trim(), email: email.trim(), seat, note } });
      onSent(`Invitation sent to ${r.email}.`);
    } catch (e) { setErr(e.detail || "Could not send the invitation."); setBusy(false); }
  }

  return (
    <div className="rm-overlay" data-testid="rm-compose" onClick={onClose}>
      <div className="rm-compose" onClick={(e) => e.stopPropagation()}>
        <div className="rm-compose-form">
          <p className="rm-eyebrow">— BEFORE IT GOES OUT</p>
          <h2 className="rm-compose-h">Send an invitation.</h2>
          <p className="rm-compose-sub">The email builds beside you as you type. Nothing goes out until
            you say so.</p>
          {/* The name is the field that is always empty and always required — focus starts there
              whether the panel was opened blank or pre-filled from a request row. */}
          <input className="rm-field" placeholder="Full name" value={name} autoFocus
                 data-testid="rm-compose-name" onChange={(e) => setName(e.target.value)} />
          <input className="rm-field" placeholder="Email address" type="email" value={email}
                 data-testid="rm-compose-email" onChange={(e) => setEmail(e.target.value)} />
          <div className="rm-field rm-field-static">
            <span className="rm-field-tag">SEAT</span>
            <span data-testid="rm-compose-seat">{seat.charAt(0).toUpperCase() + seat.slice(1)}</span>
          </div>
          <p className="rm-note-label">A LINE OF YOUR OWN · OPTIONAL</p>
          <textarea className="rm-textarea" rows={3} value={note} data-testid="rm-compose-note"
                    placeholder="One sentence, in your voice. Why this person, or where you last spoke."
                    onChange={(e) => setNote(e.target.value)} />
          <p className="rm-note-help">It sits under the opening line, before the explanation. Leave it
            empty and the email is the standard one.</p>
          <dl className="rm-meta">
            <div><dt>TO</dt><dd>{email.trim() || "their email"}</dd></div>
            <div><dt>SUBJECT</dt><dd>Your invitation to Juno</dd></div>
            <div><dt>LINK DIES</dt><dd>{preview.expires_str || "in seven days"} · after one use</dd></div>
          </dl>
          {err && <div className="gk-err" data-testid="rm-compose-err">{err}</div>}
          <div className="rm-compose-actions">
            <button className="rm-send" disabled={!ready || busy} data-testid="rm-compose-send"
                    onClick={send}>{busy ? "Sending…" : "Send it"}</button>
            <button className="rm-cancel" data-testid="rm-compose-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
        <div className="rm-compose-preview">
          <p className="rm-eyebrow rm-preview-label">— THE EMAIL</p>
          <iframe className="rm-preview-frame" title="email preview" data-testid="rm-preview-frame"
                  srcDoc={preview.html} />
        </div>
      </div>
    </div>
  );
}

// The intent preview behind Resend (founder ruling 12 Oct 2026). READ-ONLY: it shows the actual email
// that will go out, rendered server-side by the same function the send uses, and offers no way to
// change it. Juno picks the opening line — A for someone who never got to the first one, B only for
// someone who asked from the expired screen — and the sender never chooses. No editable opening line
// and no personal line: a second invitation is the standard one, said once.
function ResendPreview({ record, onClose, onSent, authedFetch }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    authedFetch(`/identity/staff/invite/${record.id}/resend/preview`)
      .then((d) => { if (live) setP(d); })
      .catch((e) => { if (live) setErr(e.detail || "Could not build the preview."); });
    return () => { live = false; };
  }, [record.id, authedFetch]);

  async function proceed() {
    if (busy || !p) return;
    setBusy(true); setErr("");
    try {
      await authedFetch("/identity/staff/invite",
        { method: "POST", body: { name: p.name, email: p.to, seat: p.seat } });
      onSent(`A fresh invitation is on its way to ${p.to}.`);
    } catch (e) { setErr(e.detail || "Could not resend the invitation."); setBusy(false); }
  }

  return (
    <div className="rm-overlay" data-testid="rm-resend-preview" onClick={onClose}>
      <div className="rm-compose" onClick={(e) => e.stopPropagation()}>
        <div className="rm-compose-form">
          <p className="rm-eyebrow">— BEFORE IT GOES OUT</p>
          <h2 className="rm-compose-h">Send it again.</h2>
          <p className="rm-compose-sub" data-testid="rm-resend-last">
            {(p && p.last_chance_line) || "This is their second and last invitation."}</p>
          <dl className="rm-meta">
            <div><dt>TO</dt><dd data-testid="rm-resend-to">{(p && p.to) || record.email}</dd></div>
            <div><dt>SUBJECT</dt><dd data-testid="rm-resend-subject">{(p && p.subject) || "—"}</dd></div>
            <div><dt>SEAT</dt><dd>{(p && p.seat) || "owner"}</dd></div>
            <div><dt>LINK DIES</dt>
              <dd>{(p && p.expires_str) || "in seven days"} · after one use</dd></div>
          </dl>
          {p && (
            <p className="rm-note-help" data-testid="rm-resend-variant">
              <strong>Juno chose this wording{p.variant ? ` (${p.variant})` : ""}.</strong>{" "}
              {p.variant_why}
            </p>
          )}
          <p className="rm-note-help">Nothing here is editable. The email below is what will arrive.</p>
          {err && <div className="gk-err" data-testid="rm-resend-err">{err}</div>}
          <div className="rm-compose-actions">
            <button className="rm-send" disabled={!p || busy} data-testid="rm-resend-proceed"
                    onClick={proceed}>{busy ? "Sending…" : "Proceed"}</button>
            <button className="rm-cancel" data-testid="rm-resend-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
        <div className="rm-compose-preview">
          <p className="rm-eyebrow rm-preview-label">— THE EMAIL</p>
          <iframe className="rm-preview-frame" title="email preview"
                  data-testid="rm-resend-frame" srcDoc={(p && p.html) || ""} />
        </div>
      </div>
    </div>
  );
}

export default function Rainmaker() {
  const { authedFetch } = useAuth();
  const [snap, setSnap] = useState(null);
  const [chip, setChip] = useState("everything");
  const [composing, setComposing] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [asks, setAsks] = useState(null);
  // The ran-out row's Resend opens an intent preview; it does not mail from the row (founder 12 Oct).
  const [resending, setResending] = useState(null);

  const load = useCallback(() => {
    authedFetch("/identity/staff/rainmaker").then(setSnap)
      .catch((e) => setErr(e.detail || "Could not load invitations."));
    // v4 Part 2: the homepage's request list. It mints nothing — you invite from it by hand.
    authedFetch("/identity/staff/invitation-requests").then(setAsks).catch(() => setAsks(null));
  }, [authedFetch]);
  useEffect(() => { load(); }, [load]);

  async function withdraw(id) {
    setErr(""); setMsg("");
    try { await authedFetch(`/identity/staff/invite/${id}/revoke`, { method: "POST" }); load(); }
    catch (e) { setErr(e.detail || "Could not withdraw that invitation."); }
  }

  // F138 (founder ruling 11 Oct): take a request off the list, or put it back. APPEND-ONLY — the row
  // stays, labelled "Dismissed", with the reason on the record. A fresh ask re-opens it server-side.
  async function dismissRequest(email) {
    const reason = window.prompt(`Why are you dismissing ${email}? (required, kept on the record)`);
    if (!(reason || "").trim()) return;
    setErr(""); setMsg("");
    try {
      await authedFetch(`/identity/staff/invitation-request/${encodeURIComponent(email)}/dismiss`,
                        { method: "POST", body: { reason, dismissed: true } });
      setMsg(`${email} is dismissed. It stays on the list, labelled.`);
      load();
    } catch (e) { setErr(e.detail || "Could not dismiss that request."); }
  }

  async function undismissRequest(email) {
    setErr(""); setMsg("");
    try {
      await authedFetch(`/identity/staff/invitation-request/${encodeURIComponent(email)}/dismiss`,
                        { method: "POST", body: { dismissed: false } });
      setMsg(`${email} is back on the list.`);
      load();
    } catch (e) { setErr(e.detail || "Could not undo that."); }
  }
  // There is NO `resend()` helper any more, deliberately. It was the one function that mailed a real
  // person straight from a click — the request row called it with no name (a 422 that blanked the
  // console) and the ran-out row called it with no preview. Both now open a panel first.

  if (err && !snap) return <div className="gk-err" data-testid="rm-err">{err}</div>;
  if (!snap) return <div className="loading" data-testid="rm-loading">Loading invitations…</div>;

  const { counts, cohorts, records } = snap;
  // F135 (founder ruling 11 Oct): a clause whose count is ZERO is DROPPED, never written out. The
  // headline used to always append "No invitations ran out.", which reads as news about nothing.
  const headline = counts.invited === 0
    ? "No invitations out yet."
    : [`${w(counts.invited)} invited.`,
       counts.filed === 0 ? "None has filed a building yet."
         : `${w(counts.filed)} ${counts.filed === 1 ? "has" : "have"} filed a building.`,
       cohorts.ran_out === 0 ? null
         : `${w(cohorts.ran_out)} ${cohorts.ran_out === 1 ? "invitation" : "invitations"} ran out.`,
      ].filter(Boolean).join(" ");
  const cohortCount = { everything: counts.invited, live: cohorts.live, ran_out: cohorts.ran_out,
    filed_nothing: cohorts.filed_nothing, gone_quiet: cohorts.gone_quiet };
  const rows = chip === "everything" ? records : records.filter((r) => r.cohort === chip);

  return (
    <div data-testid="rainmaker-view">
      <div className="rm-head">
        <div>
          <p className="rm-eyebrow">— THE RAINMAKER · INVITATIONS</p>
          <h1 className="rm-headline" data-testid="rm-headline">{headline}</h1>
          <p className="rm-lede">Release 1 is invitation-only, and this is the whole of the front door.
            Nothing goes out that you have not sent — the Rainmaker issues, tracks, and tells you who
            has gone quiet.</p>
        </div>
        <div className="rm-head-cta">
          <button className="rm-send" data-testid="rm-send-invitation-btn"
                  onClick={() => setComposing(true)}>Send an invitation</button>
          <p className="rm-cta-cap">Owner seat · seven days · one use</p>
        </div>
      </div>

      {msg && <div className="rm-ok" data-testid="rm-ok">{msg}</div>}
      {err && <div className="gk-err" data-testid="rm-err-inline">{err}</div>}

      {asks && (
        <div className="rm-asks" data-testid="rm-requests">
          {/* F138: the eyebrow counts the OPEN requests. A dismissed row stays visible below, but it
              is no longer work, so it must stop appearing in the count that says how much there is. */}
          <p className="rm-eyebrow">— ASKED FOR AN INVITATION · {asks.open_count ?? asks.count}</p>
          {(asks.open_count ?? asks.count) === 0 ? (
            <p className="rm-lede" data-testid="rm-requests-empty">Nobody has asked yet. When someone
              asks from the homepage they appear here, and you invite them by hand.</p>
          ) : (
            <p className="rm-lede">They asked from the homepage. Nothing has been sent — invite whoever
              you want from here.</p>
          )}
          {asks.count > 0 && (
          <div className="rm-asks-scroll">
          <table className="rm-table" data-testid="rm-requests-table">
            <thead>
              <tr><th>Email</th><th>Seat</th><th>Asked</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {asks.requests.map((r) => {
                const dismissed = r.state === "dismissed";
                return (
                <tr key={r.email} data-testid={`rm-request-${r.email}`}
                    className={dismissed ? "rm-request-dismissed" : ""}>
                  <td>{r.email}{r.asks > 1 && <span className="rm-chip-n">asked {r.asks}×</span>}</td>
                  <td>{r.seat}</td>
                  <td>{dateShort(r.asked_at)}</td>
                  <td data-testid={`rm-request-state-${r.email}`}>
                    {dismissed ? "Dismissed" : r.joined ? "On the desk" : r.invited ? "Invited" : "Waiting on you"}
                    {dismissed && r.dismissed_reason && (
                      <span className="rm-chip-n" title={r.dismissed_reason}>{r.dismissed_reason}</span>)}
                  </td>
                  <td className="rm-action">
                    {dismissed ? (
                      <button className="rm-linkbtn" data-testid={`rm-request-undo-${r.email}`}
                              onClick={() => undismissRequest(r.email)}>Undo</button>
                    ) : (
                      <>
                        {!r.invited && !r.joined && (
                          <button className="linkbtn" data-testid={`rm-request-invite-${r.email}`}
                                  onClick={() => setComposing({ email: r.email, seat: r.seat || "owner" })}>
                            Send an invitation
                          </button>
                        )}
                        <button className="rm-linkbtn" data-testid={`rm-request-dismiss-${r.email}`}
                                onClick={() => dismissRequest(r.email)}>Dismiss</button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          )}
        </div>
      )}

      <div className="rm-chips">
        {CHIPS.map(([key, label]) => (
          <button key={key} className={`rm-chip ${chip === key ? "rm-chip-on" : ""}`}
                  data-testid={`rm-chip-${key}`} onClick={() => setChip(key)}>
            {label}<span className="rm-chip-n">{cohortCount[key]}</span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="empty-state" data-testid="rm-empty"><p>Nobody in this cohort.</p></div>
      ) : (
        <table className="rm-table" data-testid="rm-table">
          <thead><tr>
            <th>Who</th><th>Issued</th><th>Expires</th><th>Accepted</th><th>Buildings</th>
            <th>Where they stand</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const dl = daysLeft(r.expires_at);
              return (
                <tr key={r.id} data-testid={`rm-row-${r.id}`}>
                  <td className="rm-who"><span className="rm-who-name">{r.name || "—"}</span>
                    <span className="rm-who-email">{r.email}</span></td>
                  <td className="rm-mono">{dateShort(r.issued_at)}</td>
                  <td className="rm-mono">
                    {r.accepted_at ? "—"
                      : r.cohort === "ran_out"
                        ? <span className="rm-amber">Ran out {dateShort(r.expires_at)}</span>
                        : `${dl} DAY${dl === 1 ? "" : "S"} LEFT`}
                  </td>
                  <td className="rm-mono">{r.accepted_at ? dateShort(r.accepted_at) : "NOT YET"}</td>
                  <td className="rm-mono">{r.accepted_at ? r.buildings : "—"}</td>
                  <td className="rm-stand">{r.stand}
                    {r.resend_requested && <span className="rm-asked"> · asked for a new one</span>}</td>
                  <td className="rm-action">
                    {r.cohort === "live" && (
                      <button className="rm-linkbtn" data-testid={`rm-withdraw-${r.id}`}
                              onClick={() => withdraw(r.id)}>Withdraw</button>)}
                    {r.cohort === "ran_out" && (
                      <>
                        <button className="rm-linkbtn rm-linkbtn-green" data-testid={`rm-resend-${r.id}`}
                                onClick={() => setResending(r)}>Resend</button>
                        {/* An expired invitation must be able to LEAVE the counts, or a test invite is
                            stuck in them for ever (founder 30 Sep). Withdrawing removes it from every
                            count and cohort. */}
                        <button className="rm-linkbtn" data-testid={`rm-withdraw-${r.id}`}
                                onClick={() => withdraw(r.id)}>Withdraw</button>
                      </>)}
                    {(r.cohort === "filed_nothing" || r.cohort === "gone_quiet") && (
                      <span className="rm-call">Call them</span>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {resending && (
        <ResendPreview authedFetch={authedFetch} record={resending}
                       onClose={() => setResending(null)}
                       onSent={(m) => { setResending(null); setMsg(m); load(); }} />
      )}
      {composing && (
        <Compose authedFetch={authedFetch} onClose={() => setComposing(false)}
                 initial={typeof composing === "object" ? composing : null}
                 onSent={(m) => { setComposing(false); setMsg(m); load(); }} />
      )}
    </div>
  );
}
